import { getJobCategoryColor } from '@/lib/job-list-display'
import type { PlannerModel, StoreProductVariant } from '@/types'

export function resolveServicePackageCategoryKey(variant: StoreProductVariant): string {
  if (variant.category != null && String(variant.category).trim() !== '') {
    return String(variant.category)
  }

  if (variant.category_name) {
    return variant.category_name
  }

  return 'uncategorized'
}

export function getServicePackageCategoryColor(variant: StoreProductVariant) {
  return getJobCategoryColor(resolveServicePackageCategoryKey(variant))
}

export function formatPackagePrice(value: unknown, currencySymbol = '$') {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    return '-'
  }

  return `${currencySymbol}${parsed.toFixed(2)}`
}

export function resolvePackageFlag(value: unknown) {
  if (typeof value === 'boolean') {
    return value
  }

  const normalized = String(value ?? '').trim().toLowerCase()
  return normalized === 'true' || normalized === '1' || normalized === 'yes'
}

export function isPriorityBookingEnabled(variant: StoreProductVariant) {
  const priorityFee = Number(variant.priority_fee)
  return resolvePackageFlag(variant.priority_booking_enabled) || (Number.isFinite(priorityFee) && priorityFee > 0)
}

export function isPriorityBookingDefault(variant: StoreProductVariant) {
  return resolvePackageFlag(variant.priority_booking_default)
}

export function formatPriorityBookingLabel(variant: StoreProductVariant, currencySymbol = '$') {
  const priorityFee = Number(variant.priority_fee)
  const feeLabel = Number.isFinite(priorityFee) && priorityFee > 0
    ? ` +${formatPackagePrice(priorityFee, currencySymbol)}`
    : ''

  return `${isPriorityBookingDefault(variant) ? 'Priority default' : 'Priority'} • 45 min service${feeLabel}`
}

const PROCESSING_UNIT_LABELS: Record<string, string> = {
  min: 'min',
  hr: 'hr',
  day: 'day',
}

export function formatPackageProcessingTime(
  processingTime?: string | number | null,
  processingUnit?: string | null,
) {
  const parsed = Number(processingTime)
  if (!Number.isFinite(parsed) || parsed <= 0 || !processingUnit) {
    return null
  }

  const unitLabel = PROCESSING_UNIT_LABELS[String(processingUnit).trim()] ?? String(processingUnit).trim()
  const formattedTime = Number.isInteger(parsed) ? String(parsed) : String(parsed).replace(/\.?0+$/, '')
  return `${formattedTime} ${unitLabel}`
}

export function resolveVariantSellPrice(variant: StoreProductVariant) {
  return variant.price_data?.sell_price ?? variant.sell_price ?? variant.price
}

export function resolveVariantMrpPrice(variant: StoreProductVariant) {
  return variant.price_data?.mrp_price ?? variant.mrp_price
}

export function resolveVariantPlannerModelId(variant: StoreProductVariant) {
  const candidate = variant.planner_model_id ?? variant.planner_model
  if (candidate == null || String(candidate).trim() === '') {
    return null
  }

  return String(candidate).trim()
}

export function resolveVariantPlannerModel(
  variant: StoreProductVariant,
  models: PlannerModel[],
): PlannerModel | null {
  const plannerModelId = resolveVariantPlannerModelId(variant)
  if (!plannerModelId) {
    return null
  }

  return models.find((model) => String(model.id) === plannerModelId) ?? null
}

export function normalizePlannerCommissionType(value: string | null | undefined) {
  const normalized = String(value ?? '').trim().toLowerCase().replace(/-/g, '_')
  if (normalized === 'no_pay' || normalized === 'none_pay' || normalized === 'zero' || normalized === 'none') {
    return 'none'
  }
  if (normalized === 'percentage' || normalized === 'percent' || normalized === 'pct' || normalized === '%') {
    return 'percentage'
  }
  if (normalized === 'flat' || normalized === 'flat_commission' || normalized === 'fixed') {
    return 'flat'
  }
  return 'none'
}

export function resolvePlannerCommissionConfig(model: PlannerModel | null | undefined) {
  const config = model?.config_json ?? {}
  const commissionTypeRaw = config.commission_type
    ?? config.budget_mode
    ?? config.budget_type
  const commissionValueRaw = config.commission_value
    ?? config.commission_percent
    ?? config.worker_budget_amount

  return {
    type: normalizePlannerCommissionType(typeof commissionTypeRaw === 'string' ? commissionTypeRaw : null),
    value: commissionValueRaw == null || commissionValueRaw === '' ? '' : String(commissionValueRaw),
  }
}

export function formatPlannerModelCommission(
  model: PlannerModel | null | undefined,
  currencySymbol = '$',
) {
  if (!model) {
    return 'Not set'
  }

  const { type, value } = resolvePlannerCommissionConfig(model)
  if (type === 'none' || !value.trim()) {
    return 'None'
  }

  if (type === 'percentage') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? `${parsed}%` : `${value}%`
  }

  return formatPackagePrice(value, currencySymbol)
}

export const SERVICE_PACKAGE_STATUS_BADGE = {
  active: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  inactive: 'border-slate-200 bg-slate-50 text-slate-600',
} as const
